import { PayPalButtons, PayPalScriptProvider } from '@paypal/react-paypal-js';
import { useCallback, useEffect, useRef, useState } from 'react';
import { API_BASE } from '../services/api';
import './PayPalButton.css';

function PayPalButton({ orderData, onSuccess, onError }) {
  const debugPayments = String(process.env.REACT_APP_DEBUG_PAYMENTS || '')
    .trim()
    .toLowerCase()
    .match(/^(1|true|yes|on)$/);

  const [orderId, setOrderId] = useState(null);
  const orderIdRef = useRef(null);  // Ref to avoid stale closure in async callbacks
  const [paypalClientId, setPayPalClientId] = useState(process.env.REACT_APP_PAYPAL_CLIENT_ID || null);
  const [configError, setConfigError] = useState(null);
  const [configLoading, setConfigLoading] = useState(true);

  const [debugEvents, setDebugEvents] = useState([]);

  const maskExternalId = (value) => {
    if (!value) return null;
    const s = String(value);
    if (s.length <= 10) return s;
    return `${s.slice(0, 4)}…${s.slice(-4)}`;
  };

  const pushDebug = useCallback((event, payload = {}) => {
    if (!debugPayments) return;
    const entry = {
      ts: new Date().toISOString(),
      event,
      ...payload,
    };
    setDebugEvents((prev) => [...prev, entry].slice(-50));
    // Also log to console for easier copying during QA.
    // eslint-disable-next-line no-console
    console.debug('[payments:debug]', entry);
  }, [debugPayments]);

  useEffect(() => {
    // Prefer backend-supplied config so we don't need frontend env vars.
    // Safe: returns client_id only (no secrets).
    const loadConfig = async () => {
      try {
        const resp = await fetch(`${API_BASE}/api/paypal-config`);
        let data;
        try {
          data = await resp.json();
        } catch (e) {
          data = null;
        }
        pushDebug('paypal_config.response', {
          http_status: resp.status,
          ok: resp.ok,
          success: data?.success,
          mode: data?.mode,
          currency: data?.currency,
        });
        if (data && data.success && data.client_id) {
          setPayPalClientId(data.client_id);
          setConfigError(null);
          return;
        }
        // If backend config isn't available, fall back to REACT_APP_PAYPAL_CLIENT_ID if present.
        if (!process.env.REACT_APP_PAYPAL_CLIENT_ID) {
          setConfigError(data?.message || 'PayPal is not configured.');
        }
      } catch (e) {
        if (!process.env.REACT_APP_PAYPAL_CLIENT_ID) {
          setConfigError('Failed to load PayPal configuration from backend.');
        }
      } finally {
        setConfigLoading(false);
      }
    };
    loadConfig();
  }, [pushDebug]);

  const createOrder = async (data, actions) => {
    try {
      pushDebug('orders.create.request', {
        domain: orderData?.domain,
        lead_id: orderData?.leadId,
        price: orderData?.price,
        has_offer_token: Boolean(orderData?.offerToken),
      });

      const response = await fetch(`${API_BASE}/api/orders/create`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          domain: orderData.domain,
          email: orderData.email || undefined,
          lead_id: orderData.leadId,
          price: orderData.price,
          offer_token: orderData.offerToken || undefined,
          promo_code: orderData.promoCode || undefined,
          package_type: orderData.packageType || 'domain_only',
          payment_method: 'paypal',
          company: orderData.company || undefined,
          industry: orderData.industry || undefined,
          location: orderData.location || undefined,
        }),
      });

      let result;
      try {
        result = await response.json();
      } catch (e) {
        result = null;
      }

      if (!response.ok) {
        const msg = result?.detail || result?.error || 'Failed to create order';
        pushDebug('orders.create.failed', {
          http_status: response.status,
          message: msg,
        });
        throw new Error(msg);
      }

      if (result && result.success) {
        const internalOrderId = result.order_id;
        orderIdRef.current = internalOrderId;  // Set ref for async safety
        setOrderId(internalOrderId);

        pushDebug('orders.create.ok', {
          internal_order_id: internalOrderId,
          price: result.price,
          status: result.status,
        });

        const authoritativePrice = typeof result.price === 'number'
          ? result.price
          : (orderData.price);
        const priceValue = authoritativePrice.toFixed(2);
        const currencyCode = 'USD';
        // Return PayPal order ID (if using PayPal Orders API)
        const paypalCreatePayload = {
          intent: 'CAPTURE',
          // This is a digital product; never ask for shipping address.
          application_context: {
            shipping_preference: 'NO_SHIPPING',
          },
          purchase_units: [
            {
              // IMPORTANT: bind PayPal transaction to our internal order id
              // so server-side webhook verification can reliably map payment -> order.
              custom_id: internalOrderId,
              amount: {
                currency_code: currencyCode,
                value: priceValue,
                breakdown: {
                  item_total: {
                    currency_code: currencyCode,
                    value: priceValue,
                  },
                },
              },
              description: `Domain: ${orderData.domain}`,
              items: [
                {
                  name: `Domain purchase: ${orderData.domain}`,
                  quantity: '1',
                  unit_amount: {
                    currency_code: currencyCode,
                    value: priceValue,
                  },
                  category: 'DIGITAL_GOODS',
                },
              ],
            },
          ],
        };

        pushDebug('paypal.order.create', {
          internal_order_id: internalOrderId,
          amount: priceValue,
          currency: currencyCode,
        });

        return actions.order.create(paypalCreatePayload);
      } else {
        pushDebug('orders.create.failed', {
          http_status: response.status,
          message: result?.error || 'Failed to create order',
        });
        throw new Error(result?.error || 'Failed to create order');
      }
    } catch (err) {
      if (onError) {
        onError(err.message || 'Network error');
      }
      pushDebug('orders.create.exception', {
        message: err?.message,
      });
      throw err;
    }
  };

  const onApprove = async (data, actions) => {
    try {
      pushDebug('paypal.onApprove', {
        paypal_order_id: maskExternalId(data?.orderID),
        payer_id: data?.payerID ? maskExternalId(data?.payerID) : undefined,
      });

      // Capture payment
      const details = await actions.order.capture();

      // PayPal order id is the SDK-provided orderID (more reliable than capture response shape)
      const paypalOrderId = data?.orderID || details?.id;
      const captureId = details?.purchase_units?.[0]?.payments?.captures?.[0]?.id;

      pushDebug('paypal.capture.ok', {
        paypal_order_id: maskExternalId(paypalOrderId),
        capture_id: maskExternalId(captureId),
        status: details?.status,
      });

      // Confirm payment on backend
      const response = await fetch(`${API_BASE}/api/orders/confirm`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          order_id: orderIdRef.current || orderId,  // Use ref for async safety
          // Note: backend uses paypal_order_id for (optional) server-side verification.
          paypal_order_id: paypalOrderId,
          // Kept for future audits/telemetry (backend currently ignores it).
          payment_id: captureId || paypalOrderId,
          paypal_status: details.status,
          paypal_amount: details?.purchase_units?.[0]?.amount?.value,
          paypal_currency: details?.purchase_units?.[0]?.amount?.currency_code,
          payment_data: details,
        }),
      });

      let result;
      try {
        result = await response.json();
      } catch (e) {
        const text = await response.text().catch(() => '');
        result = { success: false, error: text || 'Invalid JSON response from backend' };
      }

      pushDebug('orders.confirm.response', {
        http_status: response.status,
        ok: response.ok,
        success: result?.success,
        error: result?.error || result?.detail,
      });

      if (result.success) {
        if (onSuccess) {
          onSuccess(orderIdRef.current || orderId);
        }
      } else {
        const msg = result?.error || result?.detail || 'Payment confirmation failed';
        if (onError) onError(msg);
        throw new Error(msg);
      }
    } catch (err) {
      if (onError) {
        onError(err.message || 'Payment processing error');
      }
      pushDebug('paypal.onApprove.exception', {
        message: err?.message,
      });
      // Important: rethrow so PayPal SDK knows the approval/capture flow failed.
      throw err;
    }
  };

  const handlePayPalError = (err) => {
    pushDebug('paypal.onError', {
      message: err?.message,
      name: err?.name,
    });
    if (onError) {
      onError(err.message || 'PayPal error');
    }
  };

  return (
    <div className="paypal-button">
      {configError && (
        <div className="error-message">{configError}</div>
      )}
      {configLoading && !paypalClientId ? (
        <div className="loading" aria-live="polite">Loading payment options...</div>
      ) : null}
      {paypalClientId ? (
        <PayPalScriptProvider
          options={{
            'client-id': paypalClientId,
            currency: 'USD',
            intent: 'capture',
            // Helps surface card funding on many accounts (PayPal decides eligibility).
            'enable-funding': 'card',
          }}
        >
          <PayPalButtons
            createOrder={createOrder}
            onApprove={onApprove}
            onError={handlePayPalError}
            style={{
              layout: 'vertical',
              color: 'blue',
              shape: 'rect',
              label: 'paypal',
            }}
          />
        </PayPalScriptProvider>
      ) : null}

      {debugPayments ? (
        <details className="paypal-debug" open={false}>
          <summary>Diagnostics</summary>
          <pre>{JSON.stringify(debugEvents, null, 2)}</pre>
        </details>
      ) : null}
    </div>
  );
}

export default PayPalButton;
